import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { PyTextInput, PySelectInput } from 'components/editor/shared/components/PyMuiComponents';
import Typography from '@mui/material/Typography';
import type { SelectChangeEvent } from '@mui/material/Select';

type FormField = {
  id: string;
  label: string;
  type: 'text' | 'select';
  placeholder: string;
  options?: { label: string; value: string }[];
  isDisabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
};

type FormValue = string | number | boolean;

interface PyEntityCreationFormProps<T extends { [K in keyof T]: FormValue }> {
  formFields: FormField[];
  initialValues: T;
  onFormDataChange: (data: T) => void;
  onValidityChange?: (isValid: boolean) => void;
  textInputValidation?: {
    [K in keyof T]?: {
      onBlur?: (value: string) => void;
      onChange?: (value: string) => void;
    };
  };
  selectInputHandlers?: {
    [K in keyof T]?: (value: string) => void;
  };
}

export function PyEntityCreationForm<T extends { [K in keyof T]: FormValue }>({
  formFields,
  initialValues,
  onFormDataChange,
  textInputValidation = {},
  selectInputHandlers = {},
}: PyEntityCreationFormProps<T>) {
  const handleTextInputChange = (field: keyof T) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onFormDataChange({ ...initialValues, [field]: value });
    textInputValidation[field]?.onChange?.(value);
  };

  const handleSelectChange = (field: keyof T) => (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    onFormDataChange({ ...initialValues, [field]: value });
    selectInputHandlers[field]?.(value);
  };

  const renderInput = (field: FormField) => {
    if (field.type === 'select') {
      return (
        <FormControl fullWidth>
          <PySelectInput
            id={field.id}
            label={field.label}
            onChange={handleSelectChange(field.id as keyof T)}
            value={initialValues[field.id] || ''}
            labelId={field.id}
            disabled={field.isDisabled || false}
            placeholder={field.placeholder}
          >
            {field.options?.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </PySelectInput>
        </FormControl>
      );
    }

    return (
      <Box>
        <PyTextInput
          label={field.label}
          placeholder={field.placeholder}
          onChange={handleTextInputChange(field.id as keyof T)}
          onBlur={(e) => textInputValidation[field.id as keyof T]?.onBlur?.(e.target.value)}
          value={initialValues[field.id] || ''}
          error={field.error}
          required={field.required}
        />
        {field.error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {field.helperText}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3.5}>
        {formFields.map((field) => (
          <Grid item xs={12} sm={6} key={field.id} gap={2}>
            {renderInput(field)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
